'use client';

import { memo, useState } from 'react';

import { useCreateJoinRequestMutation } from '@/features/workspaces/api/workspacesApi';
import {
  Avatar,
  MainButton,
  SimpleTable,
  SimpleTableColumn,
  SmartInput,
} from '@web/ui';
import { notify, getErrorMessage } from '@web/utils';

type PossibleWorkspace = {
  id: string;
  name: string;
  slug: string;
  primary_domain: string;
  logo_url?: string;
};

type Props = {
  items: PossibleWorkspace[];
  isLoading?: boolean;
};

export const PossibleWorkspacesTable = memo(function PossibleWorkspacesTable({
  items,
  isLoading = false,
}: Props) {
  const [messages, setMessages] = useState<Record<string, string>>({});
  const [requested, setRequested] = useState<Record<string, boolean>>({});
  const [activeSlug, setActiveSlug] = useState<string | null>(null);
  const [locked, setLocked] = useState(false);

  const [createJoinRequest, { isLoading: isSubmitting }] =
    useCreateJoinRequestMutation();

  if (!items?.length && !isLoading) return null;

  const disabledMap = requested;

  const setMessage = (slug: string, value: string) => {
    setMessages((prev) => ({ ...prev, [slug]: value }));
  };

  const handleJoinRequest = async (slug: string) => {
    if (locked) return; // prevent multiple concurrent submissions
    setLocked(true);
    setActiveSlug(slug);

    try {
      const res = await createJoinRequest({
        workspace_slug: slug,
        message: messages[slug] || '',
      }).unwrap();

      if (res.success) {
        notify({
          message: res.message || 'Join request sent successfully.',
          type: 'success',
        });

        setRequested((prev) => ({ ...prev, [slug]: true }));
      } else {
        throw new Error(res.message || 'Failed to send join request.');
      }
    } catch (error) {
      notify({
        message: getErrorMessage(error) || 'Failed to send join request.',
        type: 'error',
      });
    } finally {
      setActiveSlug(null);
      setLocked(false);
    }
  };

  const columns: Array<SimpleTableColumn<PossibleWorkspace>> = [
    {
      header: 'Workspace',
      render: (ws) => (
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <Avatar name={ws.name} src={ws.logo_url || ''} />
          <span style={{ fontWeight: 600 }}>{ws.name}</span>
        </div>
      ),
    },
    { header: 'Primary Domain', render: (ws) => ws.primary_domain },
    {
      header: '',
      align: 'right',
      width: 380,
      render: (ws) => {
        const slug = ws.slug;
        const value = messages[slug] || '';
        const disabled =
          Boolean(disabledMap[slug]) || (locked && activeSlug !== slug);
        const loading = isSubmitting && activeSlug === slug;

        return (
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: '1fr auto',
              gap: 12,
              alignItems: 'center',
            }}
          >
            <SmartInput
              type="textarea"
              name={`join-message-${slug}`}
              label="Message to join..."
              minRows={1}
              maxRows={4}
              value={value}
              onChange={(v) => setMessage(slug, v)}
              readOnly={Boolean(disabledMap[slug])}
            />

            <MainButton
              onClick={() => handleJoinRequest(slug)}
              disabled={disabled}
              isLoading={loading}
            >
              {disabledMap[slug] ? 'Request sent' : 'Join request'}
            </MainButton>
          </div>
        );
      },
    },
  ];

  if (!items?.length && !isLoading) return null;

  return (
    <SimpleTable
      title="Other Possible Workspaces"
      columns={columns}
      data={items}
      loading={isLoading}
      getRowKey={(r) => r.id}
    />
  );
});

PossibleWorkspacesTable.displayName = 'PossibleWorkspacesTable';
